import React, { useEffect, useRef, useState } from "react";

// Each hotspot is anchored to a specific UI element on the mockup, with a
// matching `target` that the stage uses to highlight the corresponding element.
const HOTSPOTS = [
    {
        id: 1,
        target: "kpi-revenue",
        title: "Live revenue",
        body: "Revenue, orders, and AOV refreshed every minute — across every channel, every store, every region.",
    },
    {
        id: 2,
        target: "kpi-aov",
        title: "Unit economics",
        body: "Average order value, gross margin, and contribution per unit — drill from a single number to the BOM that made it.",
    },
    {
        id: 3,
        target: "chart",
        title: "Trend lines that lead",
        body: "Drill anywhere on the line: group → region → store → SKU. Every chart is a starting point, not a dead end.",
    },
    {
        id: 4,
        target: "alert",
        title: "Auto-alerts",
        body: "When revenue dips or a store stocks out, the right owner gets a WhatsApp or Slack ping with one-click drill-in.",
    },
];

export const InteractiveTour = () => {
    const [active, setActive] = useState(1);
    const hot = HOTSPOTS.find((h) => h.id === active) || HOTSPOTS[0];
    const detailRef = useRef(null);

    const onPick = (id) => {
        setActive(id);
        // On narrow viewports the detail card sits below the fold — bring it into view.
        if (typeof window !== "undefined" && window.innerWidth < 1024) {
            requestAnimationFrame(() => {
                detailRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            });
        }
    };

    useEffect(() => {
        // Cycle through hotspots if the user hasn't interacted yet for a more lively first impression.
        const t = setInterval(() => {
            setActive((prev) => (prev % HOTSPOTS.length) + 1);
        }, 5200);
        return () => clearInterval(t);
    }, []);

    return (
        <section className="section" data-testid="interactive-tour">
            <div className="container">
                <div className="section-head">
                    <span className="eyebrow">Interactive tour</span>
                    <h2 className="h2" style={{ marginTop: 18 }}>Hover the hotspots — see Mativex in action</h2>
                    <p>Click any numbered marker to explore how an operator actually uses Mativex on a typical Monday morning.</p>
                </div>

                <div className="tour-grid">
                    <div className="tour-stage" data-testid="tour-stage">
                        <div className="tour-mockup">
                            <div className="frame-chrome">
                                <span className="dot r" /><span className="dot y" /><span className="dot g" />
                                <span className="frame-url">app.scitus.ai / pulse</span>
                            </div>
                            <div className="tour-stage-inner">
                                <div className="tour-kpis">
                                    {[
                                        { l: "Revenue", v: "₹ 12.4L", c: "+18%", t: "kpi-revenue", hid: 1 },
                                        { l: "Orders", v: "284", c: "+9%", t: "kpi-orders" },
                                        { l: "AOV", v: "₹ 4,367", c: "+3%", t: "kpi-aov", hid: 2 },
                                    ].map((k) => (
                                        <div
                                            key={k.l}
                                            className={`kpi-tile tour-target ${hot.target === k.t ? "is-active" : ""}`}
                                            data-tour-target={k.t}
                                        >
                                            <div className="kpi-l">{k.l}</div>
                                            <div className="kpi-v">{k.v}</div>
                                            <div className="kpi-c">{k.c}</div>
                                            {k.hid && (
                                                <button
                                                    type="button"
                                                    className={`hotspot ${active === k.hid ? "on" : ""}`}
                                                    onClick={() => onPick(k.hid)}
                                                    onMouseEnter={() => setActive(k.hid)}
                                                    aria-label={`Hotspot ${k.hid}`}
                                                    data-testid={`tour-hotspot-${k.hid}`}
                                                >
                                                    {k.hid}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div
                                    className={`tour-chart tour-target ${hot.target === "chart" ? "is-active" : ""}`}
                                    data-tour-target="chart"
                                >
                                    <svg viewBox="0 0 320 80" preserveAspectRatio="none" width="100%" height="80">
                                        <defs>
                                            <linearGradient id="tourFill" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#00b4d8" stopOpacity="0.45" />
                                                <stop offset="100%" stopColor="#00b4d8" stopOpacity="0" />
                                            </linearGradient>
                                        </defs>
                                        <path d="M0,60 L40,52 L80,40 L120,46 L160,28 L200,34 L240,18 L280,22 L320,10 L320,80 L0,80 Z" fill="url(#tourFill)" />
                                        <polyline fill="none" stroke="#00b4d8" strokeWidth="2.4" points="0,60 40,52 80,40 120,46 160,28 200,34 240,18 280,22 320,10" />
                                    </svg>
                                    <button
                                        type="button"
                                        className={`hotspot hotspot-chart ${active === 3 ? "on" : ""}`}
                                        onClick={() => onPick(3)}
                                        onMouseEnter={() => setActive(3)}
                                        aria-label="Hotspot 3"
                                        data-testid="tour-hotspot-3"
                                    >
                                        3
                                    </button>
                                </div>
                                <div
                                    className={`tour-alert tour-target ${hot.target === "alert" ? "is-active" : ""}`}
                                    data-tour-target="alert"
                                >
                                    <span className="tour-alert-dot" />
                                    <span className="tour-alert-text">
                                        <strong>Stockout · MumBKC-04</strong> · pinged store manager via WhatsApp · 2 min ago
                                    </span>
                                    <button
                                        type="button"
                                        className={`hotspot hotspot-alert ${active === 4 ? "on" : ""}`}
                                        onClick={() => onPick(4)}
                                        onMouseEnter={() => setActive(4)}
                                        aria-label="Hotspot 4"
                                        data-testid="tour-hotspot-4"
                                    >
                                        4
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="tour-detail" data-testid="tour-detail" ref={detailRef}>
                        <div className="tour-detail-num">0{hot.id}</div>
                        <h3 className="h3" style={{ marginTop: 6, fontSize: "1.35rem" }}>{hot.title}</h3>
                        <p className="muted" style={{ marginTop: 10 }}>{hot.body}</p>
                        <div className="tour-list">
                            {HOTSPOTS.map((h) => (
                                <button
                                    key={h.id}
                                    type="button"
                                    className={`tour-list-item ${active === h.id ? "on" : ""}`}
                                    onClick={() => onPick(h.id)}
                                    data-testid={`tour-list-${h.id}`}
                                >
                                    <span className="tour-bullet">0{h.id}</span>
                                    {h.title}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default InteractiveTour;
